/**
 * BattleLogDisplay Component
 *
 * Displays the battle log in the post-game VictoryOverlay.
 * Shows a scrollable, collapsible list of all combat events from the match.
 */

import React, { useState, useRef, useEffect } from 'react';
import type { CombatLogEntry } from '../../services/combat-log-service';
import styles from './BattleLogDisplay.module.css';

// Color map for entry types (matches CombatLog component)
const TYPE_COLORS: Record<string, string> = {
  ability: '#64B5F6',   // Blue
  damage: '#EF5350',    // Red
  death: '#9E9E9E',     // Gray
  movement: '#81C784',  // Green
  buff: '#FFD54F',      // Yellow
  system: '#B39DDB',    // Purple
};

interface BattleLogDisplayProps {
  /** Log entries from the combat */
  entries: CombatLogEntry[];
  /** Whether the log starts expanded (default: false) */
  defaultExpanded?: boolean;
}

/**
 * BattleLogDisplay - Post-game battle log viewer
 *
 * Collapsible panel that shows all combat events from the match.
 * Allows players to review what happened during the battle.
 */
export function BattleLogDisplay({
  entries,
  defaultExpanded = false,
}: BattleLogDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to top when expanded
  useEffect(() => {
    if (isExpanded && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [isExpanded]);

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {/* Header - clickable to toggle */}
      <button
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="battle-log-content"
      >
        <span className={styles.headerTitle}>
          <span className={styles.headerIcon}>📜</span>
          Battle Log
          <span className={styles.entryCount}>({entries.length} events)</span>
        </span>
        <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
          ▼
        </span>
      </button>

      {/* Collapsible content */}
      <div
        id="battle-log-content"
        className={`${styles.content} ${isExpanded ? styles.contentExpanded : ''}`}
      >
        <div ref={scrollRef} className={styles.scrollArea}>
          {entries.map((entry) => (
            <LogEntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Individual log entry row
interface LogEntryRowProps {
  entry: CombatLogEntry;
}

const LogEntryRow: React.FC<LogEntryRowProps> = ({ entry }) => {
  const color = TYPE_COLORS[entry.type] || '#fff';

  return (
    <div className={styles.entryRow} style={{ borderLeftColor: color }}>
      <span className={styles.entryIcon}>{entry.icon}</span>
      <span className={styles.entryMessage}>{entry.message}</span>
      {entry.tick !== undefined && (
        <span className={styles.entryTick}>T{entry.tick}</span>
      )}
    </div>
  );
};

export default BattleLogDisplay;
